<!-- 
*****************************************************************************
**                                                                         **
** Copyright (c) 2012-2024, Tyker Technology BV                            **
** All rights reserved.                                                    **
**                                                                         **
** This software is the confidential and proprietary information of        **
** Tyker Technology BV. Unauthorized copying or disclosure of this file,   **
** via any medium is strictly prohibited. This file is only available to   **
** authorized individuals with the permission of the copyright holders.    **
** If you encounter this file and do not have permission, please contact   ** 
** the copyright holders and delete this file.                             **
**                                                                         **
***************************************************************************** 
-->

<template>
  <div id="map">
    <DevicePopup ref="devicepopup" v-on:closePopup="closeDevicePopup" />
    <RPRProductionPopup ref="productionpopup" v-on:closePopup="closeProductionPopup" />
  </div>
</template>

<script>
import DevicePopup from "./DevicePopup";
import RPRProductionPopup from "./roadprofiler/ProductionPopup";
import Task from "../models/Task";
import DXFFile from "../webcomponents/models/DXFFile";
import Motionplan from "../models/Motionplan";

import MapModel from "../webcomponents/models/map/MapModel"
import MapTaskInfo from "../webcomponents/models/map/MapElements/MapTaskInfo"
import MapGeoJson from "../webcomponents/models/map/MapElements/MapGeoJson";
import MapDXF from "../webcomponents/models/map/MapElements/MapDXF";
import MapHeightmap from "../webcomponents/models/map/MapElements/MapHeightmap";
import MapSatellite from "../webcomponents/models/map/MapElements/MapSatellite";
import MapDevices from "../webcomponents/models/map/MapElements/MapDevices";
import MapMotionplan from "../webcomponents/models/map/MapElements/MapMotionplan"
import MapChanges from "../webcomponents/models/map/MapElements/MapChanges";
import MapRPRProduction from "../webcomponents/models/map/MapElements/MapRPRProduction";
import MapRBPProduction from "../webcomponents/models/map/MapElements/MapRBPProduction";
import MapDiagnostics from "../webcomponents/models/map/MapElements/MapDiagnostics";
import MapRBPDrivingLines from "../webcomponents/models/map/MapElements/MapRBPDrivingLines"
import MapCSVNumbers from '../webcomponents/models/map/MapElements/MapCSVNumbers';

import * as CONST from "../Constants.js";
import MapSurvey from "../webcomponents/models/map/MapElements/MapSurveyViewer";

export default {
  name: "Map",

  components: { DevicePopup, RPRProductionPopup },
  data: () => ({
    devicesLoaded: false,
    task: null,
    rprloading: {
      design: false,
      current: false,
      milling: false,
      environment: false,
      production: false,
      productiontable: false,
      heightmap: false,
    },
    rbploading: { dxf: false, production: false, productiontable: false },
    vctloading: { steeringlines: false, environment: false, production: false, productiontable: false },
    surveyloading: { production: false },
    heightmapWorker: null,
    deviceFetchInterval: null,
  }),
  props: {
    data: {
      required: false,
    },
    projection: {
      required: false,
      default: "EPSG:3857",
    },
    mapUrl: {
      required: false,
      default: "https://{a-c}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    },
    rprProductionTable: {
      /* vuejs reference to productiontable */ required: true,
    },
    rbpProductionTable: {
      /* vuejs reference to productiontable */ required: true,
    },
    loading: {
      required: false,
    },
  },
  mounted: function () {
    this.map = new MapModel();
    this.map.initialize(this.$vuetify.theme.dark, this.mapUrl, this.$projections.withSiteCal, this.projection, /*showcontrols*/ false);

    this.mapTaskInfo = new MapTaskInfo(this.map, "TASKINFO", { interactable: false, group: false, defaultOn: true }, this.map);
    this.mapDevices = new MapDevices(this.map, "DEVICES", { clickmethod: 'browser', interactable: true, group: true, defaultOn: true }, this.map);
    this.mapDiagnostics = new MapDiagnostics(this.map, "DIAGNOSTICS", { interactable: true, group: true, defaultOn: true }, this.map);
    this.mapSatellite = new MapSatellite(this.map, "SATELLITE", { interactable: true, group: true, defaultOn: false }, this.map)

    this.map.addMapElement(this.mapSatellite);
    this.map.addMapElement(this.mapDevices);
    this.map.addMapElement(this.mapTaskInfo);
    this.map.addMapElement(this.mapDiagnostics);

    this.mapTaskInfo.addLayerToMap();

    this.mapTaskInfo.addElement(new MapGeoJson(this.map, "CURRENTMODEL", { interactable: true, group: true, defaultOn: false }, this.mapTaskInfo));
    this.mapTaskInfo.addElement(new MapGeoJson(this.map, "MILLINGMODEL", { interactable: true, group: true, defaultOn: false }, this.mapTaskInfo));
    this.mapTaskInfo.addElement(new MapGeoJson(this.map, "DESIGNMODEL", { interactable: true, group: true, defaultOn: true }, this.mapTaskInfo));
    this.mapTaskInfo.addElement(new MapDXF(this.map, "ENVIRONMENT", { interactable: true, group: true, defaultOn: true }, this.mapTaskInfo));
    this.mapTaskInfo.addElement(new MapHeightmap(this.map, "HEIGHTMAP", { interactable: true, group: true, defaultOn: true }, this.mapTaskInfo));
    this.mapTaskInfo.addElement(new MapChanges(this.map, "CHANGES", { interactable: true, group: false, defaultOn: false }, this.mapTaskInfo));
    this.mapTaskInfo.addElement(new MapMotionplan(this.map, "MOTIONPLAN", { clickmethod: 'browser', interactable: true, group: true, defaultOn: true }, this.mapTaskInfo));
    this.mapTaskInfo.addElement(new MapRPRProduction(this.map, "RPRPRODUCTION", { clickmethod: 'browser', interactable: true, group: true, defaultOn: true }, this.mapTaskInfo));
    this.mapTaskInfo.addElement(new MapRBPProduction(this.map, "RBPPRODUCTION", { clickmethod: 'browser', interactable: true, group: true, defaultOn: false }, this.mapTaskInfo));
    this.mapTaskInfo.addElement(new MapRBPDrivingLines(this.map, "RBPDRIVINGLINES", { interactable: true, group: true, defaultOn: false }, this.mapTaskInfo));
    this.mapTaskInfo.addElement(new MapDXF(this.map, "STEERINGLINES", { clickmethod: 'browser', interactable: true, group: false, defaultOn: true, actions: true }, this.mapTaskInfo));
    this.mapTaskInfo.addElement(new MapSurvey(this.map, "SURVEY", { interactable: true, group: true, defaultOn: true }, this.mapTaskInfo));
    this.mapTaskInfo.addElement(new MapCSVNumbers(this.map, "CSVNUMBERS", { interactable: true, group: true, defaultOn: true, }, this.mapTaskInfo));

    this.mapDevices.initializeSelect();
    this.mapDevices.addSelectEventFunction(this.deviceSelected);

    this.mapTaskInfo.getChildElement("RBPPRODUCTION").initializeSelect();
    this.mapTaskInfo.getChildElement("RBPPRODUCTION").addSelectEventFunction(this.rbpProductionSelected);

    this.mapTaskInfo.getChildElement("MOTIONPLAN").initializeSelect();
    this.mapTaskInfo.getChildElement("MOTIONPLAN").addSelectEventFunction(this.segmentSelected);

    this.mapTaskInfo.getChildElement("STEERINGLINES").initializeSelect();
    this.mapTaskInfo.getChildElement("STEERINGLINES").addSelectEventFunction(this.lineSelectedSendLineData);

    this.mapTaskInfo.getChildElement("RPRPRODUCTION").initializeSelect();
    this.mapTaskInfo.getChildElement("RPRPRODUCTION").addSelectEventFunction(this.rprProductionPopup);
  },
  computed: {
    customerId() {
      return this.$auth.customerId;
    },
  },
  watch: {
    /* user has a customer selected */
    customerId(val) {
      if (Number.isInteger(val)) {
        this.getDeviceStatus();
        this.setProjections();
        if (this.$auth.subscription == '__SUB_CIVIL_VECTOR') {
          this.mapTaskInfo.getChildElement("STEERINGLINES").addSelectEventFunction(this.lineSelectedSendLineData);
        } else {
          this.mapTaskInfo.getChildElement("STEERINGLINES").addSelectEventFunction(this.selectLayerFromLine);
        }
      }
    },
    /* only first time devices are loaded */
    devicesLoaded(val) {
      if (this.task == null && this.mapDevices.getDevices().length > 0) {
        this.map.fitExtend(this.mapDevices.getExtent(), {
          padding: [100, 100, 100, 100],
          maxZoom: 13,
        });
        this.$emit("updateLayerlist");
      }
    },
    /* projection is changed */
    projection(val) {
      this.map.setProjection(val);
    },
    rbploading: {
      handler(val) {
        if (this.loading) this.loading.addProgress(100 / 3);

        if (val.dxf && val.production && val.productiontable) {
          if (this.loading) this.loading.hide();
        }
        if (val.dxf) {
          if (this.loading) this.loading.minimize();
        }
        if (val.dxf || val.production || val.productiontable) {
          this.$emit("updateLayerlist");
          if (!val.dxf) {
            this.openRBPTask();
            return;
          }
          if (!val.production) {
            this.getRBPProduction();
            return;
          }
          if (!val.productiontable) {
            this.getRBPProductionTable();
            return;
          }
        }
      },
      deep: true,
    },
    rprloading: {
      handler(val) {
        if (this.loading) this.loading.addProgress(100 / 7);

        if (val.design && val.current && val.milling && val.environment && val.production && val.productiontable && val.heightmap) {
          if (this.loading) this.loading.hide();
        }
        if (val.design && val.current && val.milling && val.environment) {
          if (this.loading) this.loading.minimize();
        }
        if (val.design || val.current || val.milling || val.environment || val.production || val.productiontable || val.heightmap) {
          this.$emit("updateLayerlist");
          if (!val.current) {
            this.loadGeoJson("CURRENTMODEL");
            return;
          }
          if (!val.milling) {
            this.loadGeoJson("MILLINGMODEL");
            return;
          }
          if (!val.environment) {
            this.loadFile("ENVIRONMENT");
            return;
          }
          if (!val.heightmap) {
            this.getRPRHeightmap();
            return;
          }
          if (!val.production) {
            this.getRPRProduction();
            return;
          }
          if (!val.productiontable) {
            this.getRPRProductionTable();
            return;
          }
        }
      },
      deep: true,
    },
    vctloading: {
      handler(val) {
        if (this.loading) this.loading.addProgress(100 / 4);
        if (val.steeringlines && val.environment && val.production && val.productiontable) {
          if (this.loading) this.loading.hide();
        }
        if (val.steeringlines && val.environment) {
          if (this.loading) this.loading.minimize();
        }
        if (val.steeringlines || val.environment || val.production || val.productiontable || val.changes) {
          this.$emit("updateLayerlist");
          if (!val.production) {
            this.getVCTProduction();
            return;
          }
          if (!val.productiontable) {
            this.getVCTProductionTable();
            return;
          }
        }
      },
      deep: true,
    },

  },
  methods: {
    refreshProjections() {
      this.map.setProjections(this.$projections.withSiteCal);
    },
    toggleMotionplanSelect(state) {
      if (state) {
        this.mapTaskInfo.getChildElement("STEERINGLINES").addSelectEventFunction(this.lineSelectedMotionplan);
      } else {
        //remove motionplan
        this.mapTaskInfo.getChildElement("MOTIONPLAN").clear();
        this.mapTaskInfo.getChildElement("STEERINGLINES").mapElements.forEach(mapElement => {
          mapElement.show();
        })
        this.$emit("updateLayerlist");

        if (this.$auth.subscription == '__SUB_CIVIL_VECTOR') {
          this.mapTaskInfo.getChildElement("STEERINGLINES").addSelectEventFunction(this.lineSelectedSendLineData);
        } else {
          this.mapTaskInfo.getChildElement("STEERINGLINES").addSelectEventFunction(this.selectLayerFromLine);
        }
      }
    },
    selectLayerFromLine(selectEvent) {
      if (selectEvent.selected.length == 0) {
        this.$emit('setActiveLayer', null);
      } else {
        var dxfData = selectEvent.selected[0].get('dxfData');
        this.$emit('setActiveLayer', 'STEERINGLINES-' + dxfData.layer);
      }
    },
    colorSelectedLayer(layer, selected) {
      var steeringlinesElement = this.mapTaskInfo.getChildElement("STEERINGLINES");
      var layerElement = steeringlinesElement.mapElements.find(mapElement => mapElement.id == layer);
      layerElement.colorSelectedLayer(selected)
    },
    setProjections() {
      this.$axios.get(route("api.projections")).then(res => {
        this.$projections.setProjections(res.data);
        this.map.setProjections(this.$projections.withSiteCal);
        this.setCustomerProjections();
      });
    },
    async setCustomerProjections() {
      var res = await this.$axios.get(route("api.customer.customerProjections", this.customerId))
      var result = this.$projections.all.filter(projection => {
        var included = false;
        res.data.forEach(customerProjection => {
          if (customerProjection.projection_id == projection.projectionId) included = true;
        });
        return included;
      });
      this.$projections.setCustomerProjections(result);
      this.$projections.setDefaultProjectionId(0);
      res.data.forEach(customerProjection => {
        if (customerProjection.default_projection == 1) {
          this.$projections.setDefaultProjectionId(customerProjection.projection_id)
        }
      })
    },
    drawAdminDiagnostics(data, projection, fitExtend) {
      this.mapDiagnostics.drawLayer(data, projection);
      if (fitExtend)
        this.map.fitExtend(this.mapDiagnostics.getExtent(), { padding: [0, 0, 0, 0], maxZoom: 20 });

      this.$emit("updateLayerlist");
      console.log("drawdiagnostics done");
    },
    clearAdminDiagnostics() {
      this.mapDiagnostics.clear();
    },
    closeDevicePopup() {
      this.map.deviceOverlay.setPosition(undefined);
      this.deselectDevice();
    },
    closeProductionPopup() {
      this.map.productionOverlay.setPosition(undefined);
    },
    openTask(task) {
      this.mapTaskInfo.addLayerToMap();
      this.$emit("onRemoveAlert", "opentask");
      this.task = new Task(task);

      switch (this.task.operationPractice) {
        case CONST.__OP_SPRAYSTEERINGLINES:
        case CONST.__OP_SPRAYPOINTS:
        case CONST.__OP_MOTIONPLAN:

          console.info(`Open ${this.task.operationPractice}`);
          if (this.loading) this.loading.show();
          this.openRBPTask();
          break;
        case CONST.__OP_VECTOR:
          if (this.loading) this.loading.show();
          this.openVCTTask();
          break;
        case CONST.__OP_MILLINGDEPTHCONTROL:
          console.info(`Open ${this.task.operationPractice}`);
          if (this.loading) this.loading.show();
          this.openRPRTask();
          break;
        case CONST.__OP_SURVEY:
          if (this.loading) this.loading.show();
          this.openSurvey();
          break;
        default:
          console.error(`Could not open task for operation practice ${this.task.operationPractice}`);
          break;
      }
    },
    closeTask() {
      console.log("closing task");
      this.task = null;
      this.unsetLocalProjection();
      if (this.loading) this.loading.hide();
      this.mapTaskInfo.clear();

      if (this.rprProductionTable) this.rprProductionTable.empty();
      if (this.rbpProductionTable) this.rbpProductionTable.empty();
      if (this.heightmapWorker != null) {
        this.heightmapWorker.terminate();
        this.heightmapWorker = null;
      }
      this.$emit("heightmap", null);
      this.$emit("updateLayerlist");
      this.$emit("mapMotionplanEditor");
      console.log("task closed");
    },
    lineSelectedSendLineData(selectEvent) {
      if (selectEvent.selected.length == 0) {
        this.$emit('setLineData', null, null);
      } else {
        var dxfData = selectEvent.selected[0].get('dxfData');

        var postData = { lineData: { layerName: dxfData.layer, handle: dxfData.handle } };

        this.$emit('setLineData', postData, this.task.taskId);
      }
    },
    lineSelectedMotionplan(selectEvent) {
      if (selectEvent.selected.length == 0) return;


      var dxfData = selectEvent.selected[0].get('dxfData');
      //console.log(`line selected: ${id}, ${identifier}, ${name}, ${dxfData}`);
      // if (dxfData.type == "LINE") return;
      // if (dxfData.vertices.length <= 2) return;
      if (!this.$auth.isAdmin) return;

      let data = new FormData();

      data.set('line', JSON.stringify(dxfData));
      if (this.$auth.subscription == "__SUB_CIVIL_VECTOR") {
        data.set('type', "VCT");
      } else {
        data.set('type', "RBP");
      }

      this.$axios.post(route('api.motionplan.post'), data, { headers: { 'Content-Type': 'multipart/form-data' } })
        .then((res) => {
          this.mapTaskInfo.getChildElement("MOTIONPLAN").drawLayer(new Motionplan(res.data), this.task.projection);
          this.mapTaskInfo.getChildElement("STEERINGLINES").hide();
          this.$emit("updateLayerlist");
        })

      return;

      this.$axios
        .get(route("api.motionplan"), {
          params: {
            line: dxfData,
          },
        })
        .then((res) => {
          this.mapTaskInfo.getChildElement("MOTIONPLAN").drawLayer(new Motionplan(res.data), this.task.projection);
          this.$emit("updateLayerlist");
        });
    },
    segmentSelected(selectEvent) {
      var motionPlanElement = this.mapTaskInfo.getChildElement("MOTIONPLAN");

      if (selectEvent.selected.length != 0) {
        if (selectEvent.selected[0].getGeometry().getType() == "LineString") {
          this.openSegmentEditor(selectEvent.selected[0].get("IdentifierSegment"));
        } else if (selectEvent.selected[0].getGeometry().getType() == "Point") {
          this.openSegmentEditor(selectEvent.selected[0].get("IdentifierPoint"));
        }
      } else {
        if (selectEvent.deselected[0].getGeometry().getType() == "LineString") {
          motionPlanElement.selectSegment(selectEvent.deselected[0].get("IdentifierSegment"))
        } else if (selectEvent.deselected[0].getGeometry().getType() == "Point") {
          motionPlanElement.selectSegment(selectEvent.deselected[0].get("IdentifierPoint"))
        }
      }
    },
    openSegmentEditor(segmentId) {
      var motionPlanElement = this.mapTaskInfo.getChildElement("MOTIONPLAN");
      if (segmentId !== null) {
        //open 
        var segment = motionPlanElement.motionplan.data.find((segment) => segment.IdentifierSegment == segmentId);
        motionPlanElement.selectSegment(segmentId);

        if (!segment) segment = null;
        this.$emit("mapMotionplanEditor", segment);
        this.$emit("hideTaskInfoAndLayerlist");
        this.mapTaskInfo.getChildElement("ENVIRONMENT").hide();
      }
    },
    motionplanEditorClosed() {
      this.mapTaskInfo.getChildElement("MOTIONPLAN").deselectSegment();
      this.$emit("showTaskInfoAndLayerlist");
    },
    updateMotionplan(selectedSegment = null) {
      var motionPlanElement = this.mapTaskInfo.getChildElement("MOTIONPLAN");
      motionPlanElement.draw();

      if (selectedSegment) {
        this.mapTaskInfo.getChildElement("MOTIONPLAN").selectSegment(selectedSegment);
      }

    },
    rprProductionPopup(selectEvent) {
      if (selectEvent.selected.length != 0) {
        var millingInfo = selectEvent.selected["0"].get('millingInfo');
        this.$refs.productionpopup.getProductionData(millingInfo);
        this.map.showPopup(selectEvent.selected[0].getGeometry().getCoordinates()[0][0], "profilerProduction");
      }
    },
    rbpProductionSelected(selectEvent) {

      if (selectEvent.deselected != 0) this.rbpProductionTable.deselectRow();
      if (selectEvent.selected != 0) {
        this.rbpProductionTable.setSelectedFromLine(selectEvent.selected["0"].values_.name);
      }
    },
    selectRBPProductionFromList(id) { // get extend?
      var zoomLocation = this.mapTaskInfo.getChildElement("RBPPRODUCTION").SelectFeatureFromList(id);

      this.map.map.getView().setCenter(zoomLocation, this.map.map.getView().getZoom());
    },
    deviceSelected(selectEvent) {
      if (selectEvent.selected.length != 0) {
        this.$refs.devicepopup.setDeviceData(selectEvent.selected[0].get('deviceData'));
        this.map.showPopup(selectEvent.selected[0].getGeometry().getCoordinates());
      }

    },
    deselectDevice() {
      this.mapDevices.clearSelectedDevices();
      this.getDeviceStatus();
    },
    getDeviceStatus() {
      this.$axios.get(route("api.devices.status")).then((res) => {
        this.mapDevices.clear();

        if (res.data === 'No devices') return;
        res.data.forEach((device) => {
          if (device.latest_activity.locationLongitude != null)
            this.mapDevices.addDevice(
              device.accountId,
              device.deviceSerialNumberComplete,
              device,
              device.latest_activity.locationLongitude,
              device.latest_activity.locationLatitude,
              device.latest_activity.online
            );
        });

        this.devicesLoaded = true;
      });
      // get status every minute
      setTimeout(
        function () {
          this.getDeviceStatus();
        }.bind(this),
        60 * 1000
      );
    },
    dxfDrawerCallback(error, entity) {
      this.$emit("onAddAlert", "opentask", 'DXF-bestand bevat elementen die niet getekend kunnen worden.', "warning", true); // add translation
    },
    async openVCTTask() {

      this.vctloading.steeringlines = false;
      this.vctloading.environment = false;
      this.vctloading.changes = false;
      this.vctloading.production = false;
      this.vctloading.productiontable = false;

      // DXF
      var dxffile = this.task.getFileId(CONST.__RESOURCETYPE_DXF, CONST.__IDENTIFIERTYPE_STEERINGLINE);
      if (dxffile == null) {
        console.error("Could not find dxffile");
        this.$emit("onAddAlert", "opentask", this.language.t("Map.dxfAlert"), "warning", true);
        this.vctloading.steeringlines = true;
        return;
      }

      var environment = this.task.getFileId(CONST.__RESOURCETYPE_DXF, CONST.__IDENTIFIERTYPE_ENVIRONMENT);
      if (environment) {
        let result = await this.$axios.get(route("api.tasks.dxf.download", environment));
        var dxf = new DXFFile(result.data);

        var environmentElement = this.mapTaskInfo.getChildElement("ENVIRONMENT");
        environmentElement.mapElementSettings.group = false; // set group to false
        environmentElement.drawLayer(dxf, this.task.projection, 'vector', this.dxfDrawerCallback);
        this.map.fitExtend(this.mapTaskInfo.getExtent());
        this.vctloading.environment = true
      } else {
        this.vctloading.environment = true
      }

      this.$axios
        .get(route("api.tasks.dxf.download", dxffile))
        .then((res) => {
          try {
            var dxf = new DXFFile(res.data);
            this.mapTaskInfo.getChildElement("STEERINGLINES").drawLayer(dxf, this.task.projection, 'vector', 'civil', this.dxfDrawerCallback);
            this.map.fitExtend(this.mapTaskInfo.getExtent());
            this.vctloading.steeringlines = true;
          } catch (e) {
            this.$emit("onAddAlert", "opentask", this.language.t("Map.steeringlinesAlert"), "warning", true);
            console.error(e);
            this.vctloading.steeringlines = true;
          }
        })
        .catch((error) => {
          console.error(error);
          this.vctloading.steeringlines = true;
        });

      this.$axios.get(route("api.tasks.dxf.json", dxffile)).then((res) => {
        if (res.data.hasOwnProperty("actions")) {
          this.vctloading.changes = true;
        } else {
          try {
            res.data.forEach((changedata) => {
              this.mapTaskInfo.getChildElement("CHANGES").drawLayer(changedata, this.task.projection);
            });
            this.vctloading.changes = true;
          } catch (error) {
            console.log(error)
            this.vctloading.changes = true;
          }
        }
      });
    },
    async getVCTProduction() { // add driving lines
      try {
        if (this.loading) this.loading.setText(this.language.t("Map.loadingProductionData"));
        var res = await this.$axios.get(route("api.task.production.logs", this.task.taskId));
        var lines = { rptimepositionlogs: [] };
        var drivinglines = { rptimepositionlogs: [] }
        if (res.data.rptimepositionlogs) {
          if (this.task == null) return; // task is closed in the meantime
          res.data.rptimepositionlogs.forEach(function (production) {
            if (production.roadplottertaskstateId == CONST.__RP_STATE_SPRAY_ON || production.roadplottertaskstateId == CONST.__RP_STATE_SURVEY) {
              lines.rptimepositionlogs.push(production.rptimepositionlogId);
            } else if (production.roadplottertaskstateId == CONST.__RP_STATE_IDLE || production.roadplottertaskstateId == CONST.__RP_STATE_TASK_ON) {
              drivinglines.rptimepositionlogs.push(production.rptimepositionlogId);
            }
          });
          if (drivinglines.rptimepositionlogs.length > 0) {
            try {
              let resDrivingLines = await this.$axios.get(route("api.production.rbp.positions"), { params: drivinglines });
              this.mapTaskInfo.getChildElement("RBPDRIVINGLINES").drawLayer(resDrivingLines.data, this.task.projection);
              //this.map.fitExtend(this.mapTaskInfo.getExtent());
            } catch (error) {
              console.error(error)
            }
          }
          if (lines.rptimepositionlogs.length > 0)
            try {
              let resLines = await this.$axios.get(route("api.production.rbp.positions"), { params: lines });
              console.log(resLines);
              this.mapTaskInfo.getChildElement("RBPPRODUCTION").drawLayer(resLines.data, this.task.projection);
            } catch (error) {
              console.error(error)
            }
        }
        this.vctloading.production = true;
      } catch (err) {
        this.$emit("onAddAlert", "opentask", this.language.t("Map.productionAlert"), "warning", true);
        this.vctloading.production = true;
        console.error("Could not get task production: " + err);
      }
    },
    getVCTProductionTable() { // production is redrawn?
      if (this.rbpProductionTable) {
        if (this.loading) this.loading.setText(this.language.t("Map.loadingProductionData"));
        this.rbpProductionTable.getProduction(null, this.task.taskId, null, null, "GEO", this.VCTProductionTableLoaded);
      }
    },
    VCTProductionTableLoaded(success) {
      if (!success && this.task != null) {
        this.$emit("onAddAlert", "opentask", this.language.t("Map.productionTableAlert"), "warning", true);
      }
      this.vctloading.productiontable = true;
    },
    setToLocalProjection() {
      this.map.hideMapLayer();
      this.mapDevices.hide();
      this.mapSatellite.hide();
      this.mapDevices.mapElementSettings.interactable = false;
      this.mapSatellite.mapElementSettings.interactable = false;
      this.$emit("updateLayerlist");
    },
    unsetLocalProjection() {
      this.map.showMapLayer();
      this.mapDevices.mapElementSettings.interactable = true;
      this.mapSatellite.mapElementSettings.interactable = true;
      this.$emit("updateLayerlist");
      this.map.fitExtend(this.mapDevices.getExtent(), {
        padding: [100, 100, 100, 100],
        maxZoom: 13,
      });
    },
    async openRBPTask() {
      this.rbploading.dxf = false;
      this.rbploading.production = false;
      this.rbploading.productiontable = false;

      if (this.task.projection == "LOCAL") {
        this.setToLocalProjection();
      }

      if (this.task.operationPractice == CONST.__OP_MOTIONPLAN) {
        var motionplan = this.task.getFileId(CONST.__RESOURCETYPE_MOTIONPLAN, CONST.__IDENTIFIERTYPE_MOTIONPLAN);

        if (motionplan == null) {
          console.error("Could not find motionplan");
          this.$emit("onAddAlert", "opentask", this.language.t("Map.motionplanAlert"), "warning", true);
          this.rbploading.dxf = true;
          return;
        }

        var environment = this.task.getFileId(CONST.__RESOURCETYPE_DXF, CONST.__IDENTIFIERTYPE_ENVIRONMENT);
        if (environment) {
          let result = await this.$axios.get(route("api.tasks.dxf.download", environment));
          var dxf = new DXFFile(result.data);

          var environmentElement = this.mapTaskInfo.getChildElement("ENVIRONMENT");
          environmentElement.mapElementSettings.group = false; // set group to false
          environmentElement.drawLayer(dxf, this.task.projection, 'robotplotter');
          this.map.fitExtend(this.mapTaskInfo.getExtent());
        }

        this.$axios
          .get(route("api.tasks.dxf.download", motionplan))
          .then((res) => {
            var parsedMotionplan = new Motionplan(res.data);
            this.mapTaskInfo.getChildElement("MOTIONPLAN").drawLayer(parsedMotionplan, this.task.projection);

            this.map.fitExtend(this.mapTaskInfo.getExtent());
            this.rbploading.dxf = true;

          })
          .catch((error) => {
            console.error(error);
          });
      } else {
        // DXF
        var dxffile = this.task.getFileId(CONST.__RESOURCETYPE_DXF, CONST.__IDENTIFIERTYPE_STEERINGLINE);

        var hasCSV = this.task.getFileId(CONST.__RESOURCETYPE_CSV, CONST.__IDENTIFIERTYPE_SPRAYPOINTS) ? true : false;


        if (dxffile == null) {
          console.error("Could not find dxffile");
          this.$emit("onAddAlert", "opentask", this.language.t("Map.dxfAlert"), "warning", true);
          this.rbploading.dxf = true;
          return;
        }

        if (this.task.operationPractice == CONST.__OP_VECTOR) {
          var environment = this.task.getFileId(CONST.__RESOURCETYPE_DXF, CONST.__IDENTIFIERTYPE_ENVIRONMENT);
          if (environment) {
            let result = await this.$axios.get(route("api.tasks.dxf.download", environment));
            var dxf = new DXFFile(result.data);

            var environmentElement = this.mapTaskInfo.getChildElement("ENVIRONMENT");
            environmentElement.mapElementSettings.group = false; // set group to false
            environmentElement.drawLayer(dxf, this.task.projection, 'vector');
            this.map.fitExtend(this.mapTaskInfo.getExtent());
          }
        }
        else if (this.task.operationPractice == CONST.__OP_SPRAYPOINTS) {
          var points = this.task.getFileId(CONST.__RESOURCETYPE_CSV, CONST.__IDENTIFIERTYPE_SPRAYPOINTS);
          if (points) {
            let result = await this.$axios.get(route("api.tasks.dxf.download", points));

            if (result.data.split('\r\n')[0].toLowerCase() != "pointname,x1,y1,z1") {
              result.data = "PointName,X1,Y1,Z1\r\n" + result.data
            }

            result.data = result.data.charAt(0).toUpperCase() + result.data.slice(1, 5) + result.data.charAt(5).toUpperCase() + result.data.slice(6);


            const [keys, ...rest] = result.data
              .replaceAll('\r', '')
              .trim()
              .split("\n")
              .map((item) => item.split(','));

            const formedArr = rest.map((item) => {
              const object = {};
              keys.forEach((key, index) => (object[key] = item.at(index)));
              return object;
            });
            this.mapTaskInfo.getChildElement("CSVNUMBERS").drawLayer(formedArr, this.task.projection, "right");
          }
          var environment = this.task.getFileId(CONST.__RESOURCETYPE_DXF, CONST.__IDENTIFIERTYPE_ENVIRONMENT);
          if (environment) {
            let result = await this.$axios.get(route("api.tasks.dxf.download", environment));
            var dxf = new DXFFile(result.data);

            var environmentElement = this.mapTaskInfo.getChildElement("ENVIRONMENT");
            environmentElement.mapElementSettings.group = false; // set group to false
            environmentElement.drawLayer(dxf, this.task.projection, "spraypointsEnvironment");
            this.map.fitExtend(this.mapTaskInfo.getExtent());
          }
        }
        this.$axios
          .get(route("api.tasks.dxf.download", dxffile))
          .then((res) => {
            try {
              var dxf = new DXFFile(res.data);
              this.mapTaskInfo.getChildElement("STEERINGLINES").drawLayer(dxf, this.task.projection, hasCSV ? 'spraypoints' : 'robotplotter', 'civil', this.dxfDrawerCallback);
              this.map.fitExtend(this.mapTaskInfo.getExtent());
              this.rbploading.dxf = true;
            } catch (e) {
              this.$emit("onAddAlert", "opentask", this.language.t("Map.steeringlinesAlert"), "warning", true);
              console.error(e);
              this.rbploading.dxf = true;
            }
          })
          .catch((error) => {
            console.error(error);
            this.rbploading.dxf = true;
          });

        this.$axios.get(route("api.tasks.dxf.json", dxffile)).then((res) => {
          if (res.data[0].actions.length > 0) {
            if (res.data.hasOwnProperty("actions")) {
            } else {
              try {
                res.data.forEach((changedata) => {
                  this.mapTaskInfo.getChildElement("CHANGES").drawLayer(changedata, this.task.projection);
                });
              } catch (error) {
              }
            }
          }
        });
      }
    },
    openSurvey() {
      this.getSurveyProduction();
    },
    async getSurveyProduction() {
      try {
        if (this.loading) this.loading.setText(this.language.t("Map.loadingSurvey"));
        if (this.loading) this.loading.minimize();

        if (this.loading) this.loading.addProgress(33);
        let res = await this.$axios.get(route("api.tasks.opensurvey", this.task.taskId));

        if (this.loading) this.loading.addProgress(66);
        var timepositions = {};

        res.data.forEach((production) => {
          if (production.surveytimepositionlogId) {
            Object.assign(timepositions, { [production.surveytimepositionlogId]: production.timepositions });
          } else {
            Object.assign(timepositions, { [production.rptimepositionlogId]: production.timepositions });
          }
        });


        this.mapTaskInfo.getChildElement("SURVEY").drawLayer(timepositions, this.task.projection);
        this.map.fitExtend(this.mapTaskInfo.getExtent());

        this.$emit("updateLayerlist");

        if (this.loading) this.loading.hide();
      } catch (err) {
        console.error("Could not get survey production: " + err);
      }
    },
    async openRPRTask() {
      this.rprloading.design = false;
      this.rprloading.current = false;
      this.rprloading.environment = false;
      this.rprloading.production = false;
      this.rprloading.productiontable = false;
      this.rprloading.heightmap = false;

      this.loadGeoJson("DESIGNMODEL");
    },
    loadGeoJson(key) {
      console.log(`Draw layer ${key}`);
      switch (key) {
        case "DESIGNMODEL":
          {
            if (this.loading) this.loading.setText(this.language.t("Map.loadingDesignModel"));
            let dxffile = null;
            dxffile = this.task.getFileId(CONST.__RESOURCETYPE_DXF, CONST.__IDENTIFIERTYPE_DESIGNMODEL);
            if (dxffile != null) {
              this.$axios
                .get(route("api.tasks.dxf.download.geojson", dxffile))
                .then((res) => {
                  console.log(res);
                  if (this.task == null) return; // task is closed in the meantime
                  try {
                    this.mapTaskInfo.getChildElement("DESIGNMODEL").drawLayer(this.task.projection, res.data, this.geojsonLoaded);
                    //this.mapTaskInfo.drawGeoJsonVT("DESIGNMODEL", "Ontwerp model", res.data, false, true, "profiler", this.task.projection, this.geojsonLoaded);
                  } catch (e) {
                    this.$emit("onAddAlert", "opentask", this.language.t("Map.designModelAlert"), "warning", true);
                    this.rprloading.design = true;
                    console.error("Could not draw design model");
                    console.log(e);
                  }
                })
                .catch((err) => {
                  console.error(err);
                  this.$emit("onAddAlert", "opentask", this.language.t("Map.designModelAlert"), "warning", true);
                  this.rprloading.design = true;
                });
            } else {
              console.log("designmodel not found");
              this.rprloading.design = true;
            }
          }
          break;
        case "CURRENTMODEL":
          {
            if (this.loading) this.loading.setText(this.language.t("Map.loadingCurrentModel"));
            let dxffile = this.task.getFileId(CONST.__RESOURCETYPE_DXF, CONST.__IDENTIFIERTYPE_CURRENTMODEL);
            if (dxffile != null) {
              this.$axios
                .get(route("api.tasks.dxf.download.geojson", dxffile))
                .then((res) => {
                  if (this.task == null) return; // task is closed in the meantime
                  try {
                    this.mapTaskInfo.getChildElement("CURRENTMODEL").drawLayer(this.task.projection, res.data, this.geojsonLoaded);
                  } catch (e) {
                    this.$emit("onAddAlert", "opentask", this.language.t("Map.currentModelAlert"), "warning", true);
                    this.rprloading.current = true;
                    console.error("Could not draw current model");
                    console.log(e);
                  }
                })
                .catch((err) => {
                  console.error(err);
                  this.$emit("onAddAlert", "opentask", this.language.t("Map.currentModelAlert"), "warning", true);
                  this.rprloading.current = true;
                });
            } else {
              console.log("currentmodel not found");
              this.rprloading.current = true;
            }
          }
          break;
        case "MILLINGMODEL":
          {
            if (this.loading) this.loading.setText(this.language.t("Map.loadingMillingModel"));
            let dxffile = this.task.getFileId(CONST.__RESOURCETYPE_DXF, CONST.__IDENTIFIERTYPE_MILLINGMODEL);
            if (dxffile != null) {
              this.$axios
                .get(route("api.tasks.dxf.download.geojson", dxffile))
                .then((res) => {
                  if (this.task == null) return; // task is closed in the meantime
                  try {
                    this.mapTaskInfo.getChildElement("MILLINGMODEL").drawLayer(this.task.projection, res.data, this.geojsonLoaded);
                    //this.mapTaskInfo.drawLayer("MILLINGMODEL", this.task.projection, res.data, this.geojsonLoaded);
                  } catch (e) {
                    this.$emit("onAddAlert", "opentask", this.language.t("Map.millingModelAlert"), "warning", true);
                    this.rprloading.milling = true;
                    console.error("Could not draw milling model");
                    console.log(e);
                  }
                })
                .catch((err) => {
                  console.log(err);
                  this.$emit("onAddAlert", "opentask", this.language.t("Map.millingModelAlert"), "warning", true);
                  this.rprloading.milling = true;
                });
            } else {
              console.log("millingmodel not found");
              this.rprloading.milling = true;
            }
          }
          break;
        default:
          console.log(`undefined option: ${key}`);
          break;
      }
    },
    geojsonLoaded(layerid) {
      switch (layerid) {
        case "DESIGNMODEL":
          this.rprloading.design = true;
          break;
        case "CURRENTMODEL":
          this.rprloading.current = true;
          break;
        case "MILLINGMODEL":
          this.rprloading.milling = true;
          break;
        case "ENVIRONMENT":
          this.rprloading.environment = true;
          break;
        default:
          console.log(`undefined option: ${layerid}`);
          break;
      }
      this.map.fitExtend(this.mapTaskInfo.getExtent());
    },
    loadFile(key) {
      console.log(`Draw layer ${key}`);
      switch (key) {
        case "ENVIRONMENT":
          {
            if (this.loading) this.loading.setText(this.language.t("Map.loadingEnvironmentModel"));
            let dxffile = this.task.getFileId(CONST.__RESOURCETYPE_DXF, CONST.__IDENTIFIERTYPE_ENVIRONMENT);
            if (dxffile != null) {
              this.$axios
                .get(route("api.tasks.dxf.download", dxffile))
                .then((res) => {
                  if (this.task == null) return; // task is closed in the meantime
                  try {
                    let dxf = new DXFFile(res.data);
                    this.mapTaskInfo.getChildElement("ENVIRONMENT").drawLayer(dxf, this.task.projection, "roadprofiler");
                    this.map.fitExtend(this.mapTaskInfo.getExtent());
                    this.rprloading.environment = true;
                  } catch (e) {
                    this.$emit("onAddAlert", "opentask", this.language.t("Map.environmentModelAlert"), "warning", true);
                    console.error(e);
                    this.rprloading.environment = true;
                  }
                })
                .catch((error) => {
                  console.error(error);
                  this.rprloading.environment = true;
                });
            } else {
              this.rprloading.environment = true;
            }
          }
          break;
        default:
          console.log(`undefined option: ${key}`);
          break;
      }
    },
    async getRBPProduction() {
      try {
        if (this.loading) this.loading.setText(this.language.t("Map.loadingProductionData"));
        let res = await this.$axios.get(route("api.task.production.logs", this.task.taskId));
        var lines = { rptimepositionlogs: [] };
        var points = { rptimepositionlogs: [] };
        var drivinglines = { rptimepositionlogs: [] }
        if (res.data.rptimepositionlogs) {
          if (this.task == null) return; // task is closed in the meantime
          res.data.rptimepositionlogs.forEach((production) => {
            if (production.roadplottertaskstateId == CONST.__RP_STATE_SPRAY_ON || production.roadplottertaskstateId == CONST.__RP_STATE_SURVEY) {
              lines.rptimepositionlogs.push(production.rptimepositionlogId);
            } else if (production.roadplottertaskstateId == CONST.__RP_STATE_POINT) {
              points.rptimepositionlogs.push(production.rptimepositionlogId);
            } else if (production.roadplottertaskstateId == CONST.__RP_STATE_IDLE || production.roadplottertaskstateId == CONST.__RP_STATE_TASK_ON) {
              drivinglines.rptimepositionlogs.push(production.rptimepositionlogId);
            }
          });

          if (drivinglines.rptimepositionlogs.length > 0) {
            try {
              let resDrivingLines = await this.getRpTimePositionLogs(drivinglines.rptimepositionlogs);
              this.mapTaskInfo.getChildElement("RBPDRIVINGLINES").drawLayer(resDrivingLines, this.task.projection);
              // this.map.fitExtend(this.mapTaskInfo.getExtent());
            } catch (error) {
              console.error(error)
            }
          }

          if (lines.rptimepositionlogs.length > 0) {
            try {
              let resLines = await this.getRpTimePositionLogs(lines.rptimepositionlogs);
              this.mapTaskInfo.getChildElement("RBPPRODUCTION").drawLayer(resLines, this.task.projection);

              //get extend?
            } catch (error) {
              console.error(error)
            }
          }

          if (points.rptimepositionlogs.length > 0) {
            try {
              let resPoints = await this.getRpTimePositionLogs(points.rptimepositionlogs);
              this.mapTaskInfo.getChildElement("RBPPRODUCTION").drawLayer(resPoints, this.task.projection, 'points');
              // this.map.fitExtend(this.mapTaskInfo.getExtent());
            } catch (error) {
              console.error(error)
            }
          }

        } else { //no positions?
          this.rbploading.productiontable = true;
          this.rbploading.production = true;
        }

        this.rbploading.production = true;
      } catch (err) {
        this.$emit("onAddAlert", "opentask", this.language.t("Map.productionAlert"), "warning", true);
        this.rbploading.production = true;
        console.error("Could not get task production: " + err);
      }
    },
    async getRpTimePositionLogs(rptimepositionlogIds) {
      var deferreds = {};
      var rptimepositionlogsToHandle = [];
      var maxRpTimePositionlogsToHandle = 100;
      var rpTimePositionLogsHandled = 0;
      var promises = [];

      // Iteratie over RpTimePositionLogs
      var i = 0;
      var len = rptimepositionlogIds.length;
      for (let j = 0; j < rptimepositionlogIds.length; j++) {
        rptimepositionlogsToHandle.push(rptimepositionlogIds[j]);
        rpTimePositionLogsHandled++;

        if (rpTimePositionLogsHandled > maxRpTimePositionlogsToHandle || i === len - 1) {
          promises.push(this.addRpTimePositionLogs(rptimepositionlogsToHandle).then(res => {
            deferreds = Object.assign(deferreds, res);
          }));
          rptimepositionlogsToHandle = [];
          rpTimePositionLogsHandled = 0;
        }
        i++;
      }
      await Promise.all(promises)
      return deferreds;
    },
    async addRpTimePositionLogs(rptimepositionlogIds) {
      var res = await this.$axios.get(route("api.production.rbp.positions"), { params: { rptimepositionlogs: rptimepositionlogIds } });
      console.log(res);
      return res.data;
    },
    getRBPProductionTable() { // asdasd
      if (this.rbpProductionTable) {
        if (this.loading) this.loading.setText(this.language.t("Map.loadingProductionData"));
        this.rbpProductionTable.getProduction(null, this.task.taskId, null, null, "GEO", this.RBProductionTableLoaded);
      }
    },
    RBProductionTableLoaded(success) {
      if (!success && this.task != null) {
        this.$emit("onAddAlert", "opentask", this.language.t("Map.productionTableAlert"), "warning", true);
      }
      this.rbploading.productiontable = true;
    },
    async getRPRProduction() {
      try {
        if (this.loading) this.loading.setText(this.language.t("Map.loadingProductionData"));
        let res = await this.$axios.get(route("api.task.production.logs", this.task.taskId));
        var params = { mctimepositionlogs: [] };
        if (res.data.mctimepositionlogs.length > 0) {
          if (this.task == null) return; // task is closed in the meantime

          res.data.mctimepositionlogs.forEach(function (production) {
            params.mctimepositionlogs.push(production.mctimepositionlogId);
          });

          let res2 = await this.$axios.get(route("api.production.rpr.positions"), {params: params});
          if (this.task == null) return; // task is closed in the meantime
          if (Object.keys(res2.data).length > 0) {
            this.mapTaskInfo.getChildElement("RPRPRODUCTION").drawLayer(res2.data, this.task.projection);
          }
          this.rprloading.production = true;
        } else {
          //no timepositionlogs so no production
          this.rprloading.production = true;
        }
      } catch (err) {
        this.$emit("onAddAlert", "opentask", this.language.t("Map.productionAlert"), "warning", true);
        this.rprloading.production = true;
        console.error("Could not get task production: " + err);
      }
    },
    getRPRProductionTable() {
      if (this.rprProductionTable) {
        if (this.loading) this.loading.setText(this.language.t("Map.loadingProductionData"));
        this.rprProductionTable.getProduction(null, this.task.taskId, null, null, this.RPRProductionTableLoaded);
      }
    },
    RPRProductionTableLoaded(success) {
      if (!success && this.task != null) {
        this.$emit("onAddAlert", "opentask", this.language.t("Map.productionTableAlert"), "warning", true);
      }
      console.log(success);
      this.rprloading.productiontable = true;
    },
    async getRPRHeightmap() {
      if (!indexedDB) {
        this.$emit("onAddAlert", "opentask", this.language.t("Map.localStorageAlert"), "warning", true);
        this.rprloading.heightmap = true;
        return;
      } else {
        console.log(indexedDB);
      }

      console.log("Draw Heightmap");
      if (this.loading) this.loading.setText(this.language.t("Map.loadingHeightmap"));

      this.heightmapWorker = new Worker("/workers/LoadHeightMap.js");
      this.heightmapWorker.onmessage = function (event) {
        console.log("Heightmap loaded");
        if (event.data != null) {
          console.log("Draw heightmap");
          this.mapTaskInfo.getChildElement("HEIGHTMAP").drawLayer(event.data, this.task.projection, this.heightmapLoaded); //(layerId, projection, data, callback = null)
        } else {
          this.$emit("onAddAlert", "opentask", this.language.t("Map.taskTooLargeAlert"), "warning", true);
          this.rprloading.heightmap = true;
          console.error("Taak is te groot om freescijvers in overzicht weer te geven");
        }
      }.bind(this);
      var heightmap = this.task.getFileId(CONST.__RESOURCETYPE_DXF, CONST.__IDENTIFIERTYPE_HEIGHTMAP);
      if (heightmap == null) {
        this.$emit("onAddAlert", "opentask", this.language.t("Map.heightmapAlert"), "warning", true);
        this.rprloading.heightmap = true;
        console.error("Could not find heightmap");
        return;
      }
      this.$axios
        .get(route("api.tasks.dxf.download", heightmap))
        .then((res) => {
          if (this.heightmapWorker == null) return; // task is closed in the meantime
          this.heightmapWorker.postMessage({
            heightmap: res.data,
          });
        })
        .catch((error) => {
          console.error(error);
          this.rprloading.heightmap = true;
          this.$emit("onAddAlert", "opentask", this.language.t("Map.heightmapAlert"), "warning", true);
        });
    },
    heightmapLoaded(success) {
      if (success) {
        this.$emit("heightmap", this.mapTaskInfo.getChildElement("HEIGHTMAP").getData());
        this.$emit("updateLayerlist");
      } else {
        this.$emit("onAddAlert", "opentask", this.language.t("Map.heightmapAlert"), "warning", true);
      }
      this.rprloading.heightmap = true;
    },
    setHeightmap(settings) {
      this.mapTaskInfo.getChildElement("HEIGHTMAP").setSettings(settings);
      this.$emit("updateLayerlist");
      this.mapTaskInfo.getChildElement("HEIGHTMAP").draw();
    },
  },
};
</script>

<style>
#map {
  width: 100%;
  height: 100%;
}

.rotate-north {
  top: 65px;
  left: 0.5em;
}

.ol-touch .rotate-north {
  top: 80px;
}

.layer-switcher .panel {
  background-color: var(--v-base-base) !important;
  color: var(--v-text-base);
}

.ol-attribution:not(.ol-collapsed) {
  background-color: var(--v-base-base) !important;
}

.ol-attribution ul {
  color: var(--v-text-base);
}

.ol-zoom {
  top: unset;
  left: unset;
  right: 8px;
  bottom: 8px;
}
</style>